import { DotsVertical } from 'mdi-material-ui';
import React from 'react';
import { CommonDataProps, SxProps } from '../../utils';
import { Box } from '../Box';
import { IconButton, IconButtonProps } from '../IconButton';
import { Menu, MenuProps } from '../Menu';

// NOTE: this component was recent moved into `chrono-ui` from `cloud` and therefore may not follow
// all chrono-ui best practices. We are slowly working on fixing that.

export interface MoreActionsProps extends IconButtonProps, Pick<MenuProps, 'dangerouslyKeepMounted'>, CommonDataProps {
  children?: React.ReactNode;
  // Allows for more fine grain control after a user interacts with the content
  // This takes precedence over `children` if both are defined
  Content?: (closePopover: () => void) => React.ReactNode;
  contentSx?: SxProps;
  Icon?: React.ElementType;
  // When a user selects an option, the default is for the menu to close.
  preserveMenuOnClick?: boolean;
}

/**
 * The "more actions" pattern is a combination of a button icon (usually with a kebab-style three dots) and a `Menu`
 * that should be used to present the user with more actions to perform.
 *
 * This is useful in cases where the actions would take up too much space if presented inline via buttons.
 */
export function MoreActions({
  children,
  preserveMenuOnClick,
  size,
  Content,
  Icon,
  contentSx,
  dangerouslyKeepMounted,
  ...props
}: MoreActionsProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLButtonElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const additionalProps = preserveMenuOnClick ? {} : { onClick: handleClose };

  return (
    <>
      <IconButton
        {...props}
        aria-haspopup="true"
        aria-label="See more actions"
        onClick={handleClick}
        data-testid="moreActionsButton"
        size={size}
      >
        {Icon ? <Icon /> : <DotsVertical />}
      </IconButton>
      <Menu
        {...additionalProps}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        ariaLabel="More actions menu"
        dangerouslyKeepMounted={dangerouslyKeepMounted}
      >
        <Box sx={contentSx}>{Content ? Content(handleClose) : children}</Box>
      </Menu>
    </>
  );
}
